import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  finalize,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import {
  EntityInfoService,
  GroupingEntity,
} from 'src/app/shared/services/metadata-api/entity-info.service';
import {
  EntitySearchHit,
  SearchService,
} from 'src/app/shared/services/metadata-api/search.service';
import { AiSearchService } from 'src/app/shared/services/ai-search/ai-search.service';
import {
  CompanyFormData,
  CompanyQueryRequest,
  EnrichmentResult,
  EntityEnrichmentRequest,
} from 'src/app/shared/models/ai-search.interface';

export { CompanyFormData } from 'src/app/shared/models/ai-search.interface';

interface FreeTextOption {
  _freeText: string;
}

type CompanySearchOption = EntitySearchHit | FreeTextOption;

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-company-form',
  templateUrl: './company-form.component.html',
  styleUrls: ['./company-form.component.scss'],
})
export class CompanyFormComponent implements OnInit, OnDestroy {
  @Input() initialLegalName = '';
  @Input() autoSetAsNewCompany = false;
  @Input() userEmail = '';
  @Input() project = '';
  @Input() legalNamePlaceholder = '';
  @Input() nativeInput = false;
  /**
   * Class for the dropdown panels' CDK overlay. They attach to <body>, outside
   * the host that declares the palette, so the host has to name it —
   * `dd-overlay-panel` or `setup-overlay-panel`.
   */
  @Input() panelClass = '';
  @Output() submitForm = new EventEmitter<CompanyFormData>();
  @Output() navigateToEntity = new EventEmitter<{
    type: string;
    id: string;
  }>();
  @Output() searchStateChange = new EventEmitter<{
    enabled: boolean;
    enriching: boolean;
  }>();

  enriching = false;

  legalNameControl = new UntypedFormControl('');
  companySearchResults$: Observable<EntitySearchHit[]>;
  selectedEntity: EntitySearchHit['entity'] | null = null;
  isNewCompany = false;

  legalName = '';
  website = '';

  countryControl = new UntypedFormControl('');
  countries: GroupingEntity[] = [];
  filteredCountries: Observable<GroupingEntity[]>;

  private destroy$ = new Subject<void>();

  constructor(
    private entityInfoService: EntityInfoService,
    private searchService: SearchService,
    private aiSearchService: AiSearchService,
  ) {}

  ngOnInit(): void {
    if (this.initialLegalName) {
      this.legalNameControl.setValue(this.initialLegalName);
      if (this.autoSetAsNewCompany) {
        this.isNewCompany = true;
        this.legalName = this.initialLegalName;
        this.countryControl.enable();
        setTimeout(() => this.emitSearchState());
      }
    }
    this.setupCompanySearch();
    this.loadCountries();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Display functions ---

  displayCompanySearch = (
    option: CompanySearchOption | string | null,
  ): string => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    if ('_freeText' in option) return option._freeText;
    return option.entity?.name || '';
  };

  displayGroupingEntity(entity: GroupingEntity | string): string {
    if (!entity) return '';
    if (typeof entity === 'string') return entity;
    return entity.name || '';
  }

  // --- Getters ---

  get selectedCountry(): GroupingEntity | null {
    const val = this.countryControl.value;
    return val && typeof val === 'object' ? val : null;
  }

  get legalNameText(): string {
    return this.displayCompanySearch(this.legalNameControl.value);
  }

  /** Enough typed to be worth offering as a company of its own. */
  get hasFreeTextAction(): boolean {
    return this.legalNameText.length >= 3;
  }

  /**
   * The dropdowns open in a CDK overlay attached to <body>, outside the host
   * that declares the palette — so the host names it (`panelClass`) and it
   * travels with the panel. `--lead` tells the column captions to stick below
   * the action row rather than at the top of the panel.
   */
  get companySearchPanelClass(): string {
    return [
      'company-search-panel',
      this.hasFreeTextAction ? 'company-search-panel--lead' : '',
      this.panelClass,
    ]
      .filter(Boolean)
      .join(' ');
  }

  get countryPanelClass(): string {
    return ['cf-plain-panel', this.panelClass].filter(Boolean).join(' ');
  }

  get isSearchEnabled(): boolean {
    if (this.enriching) return false;
    if (this.legalNameText.trim().length === 0) return false;
    if (this.selectedEntity) return true;
    return this.website.trim().length > 0 && !!this.selectedCountry;
  }

  // --- Search step handlers ---

  emitSearchState(): void {
    this.searchStateChange.emit({
      enabled: this.isSearchEnabled,
      enriching: this.enriching,
    });
  }

  onCompanySearchSelect(option: CompanySearchOption): void {
    if ('_freeText' in option) {
      this.selectedEntity = null;
      this.isNewCompany = true;
      this.legalName = option._freeText;
      this.countryControl.enable();
      // Overwrite Material's object-valued selection with a plain string so
      // subsequent typing reports string values through valueChanges.
      setTimeout(() => this.legalNameControl.setValue(this.legalName));
    } else {
      this.selectedEntity = option.entity;
      this.isNewCompany = false;
      this.legalName = option.entity.name;
      this.countryControl.disable();
    }
    this.emitSearchState();
  }

  onLegalNameInput(): void {
    this.selectedEntity = null;
    this.isNewCompany = false;
    this.countryControl.enable();
    this.emitSearchState();
  }

  onSearch(): void {
    if (!this.isSearchEnabled) return;

    if (this.selectedEntity) {
      this.navigateToEntity.emit({
        type: this.selectedEntity.type,
        id: this.selectedEntity.alpha_id,
      });
      return;
    }

    this.legalName = this.legalNameText;
    this.enriching = true;
    this.legalNameControl.disable();
    this.emitSearchState();

    const request: EntityEnrichmentRequest = {
      companies: [
        {
          name: this.legalName.trim(),
          country: this.selectedCountry?.alpha_id?.toUpperCase() || undefined,
          website: this.website.trim() || undefined,
        },
      ],
      skip_llm: false,
    };

    this.aiSearchService
      .generateEntityEnrichment(request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.enriching = false;
          this.legalNameControl.enable();
          this.emitSearchState();
        }),
      )
      .subscribe({
        next: (response) => this.buildAndSubmit(response.results?.[0] ?? null),
        error: (err) => {
          // Proceed on the typed values alone. The query will be weaker than an
          // enriched one (no identifiers, exchanges or name forms), but a
          // generated query beats a dead end.
          console.warn('Entity enrichment failed, proceeding without it:', err);
          this.buildAndSubmit(null);
        },
      });
  }

  // --- Private ---

  private buildAndSubmit(result: EnrichmentResult | null): void {
    // Enrichment's company_request IS the generate/company body — forward it
    // untouched. The literal covers a failed, empty, or unusable enrichment
    // (legal_name is the one field generate/company cannot do without).
    const enriched = result?.company_request;
    const request: CompanyQueryRequest = enriched?.legal_name
      ? enriched
      : {
          legal_name: this.legalName.trim(),
          company_type: 'public',
          ...(this.selectedCountry?.name
            ? { country: this.selectedCountry.name }
            : {}),
          ...(this.website.trim() ? { website: this.website.trim() } : {}),
        };

    const entityRequestData = [
      {
        legal_name: request.legal_name,
        additional_information: {
          time: Date.now(),
          email: this.userEmail,
          project: this.project,
          message: '',
          sector: request.sector || '',
          // The ticket keeps what the user typed when enrichment omitted it —
          // absent keys are normal in the request, but a blank field here would
          // lose input the form insisted on.
          website: request.website || this.website.trim(),
          surfaceForms: (request.surface_forms || []).join(),
          city: request.city || '',
          country: request.country || this.selectedCountry?.name || '',
          isin: (request.isins || []).join(),
          ceo: (request.key_persons || []).join(', '),
        },
      },
    ];

    this.submitForm.emit({ request, entityRequestData });
  }

  private setupCompanySearch(): void {
    this.companySearchResults$ = this.legalNameControl.valueChanges.pipe(
      debounceTime(300),
      switchMap((value) => {
        // Only search when user is typing (string value).
        // Object values mean something was selected — don't re-search.
        if (typeof value !== 'string' || value.length < 2) return of([]);
        return this.searchService.search(value, false).pipe(
          map((res: { hits?: EntitySearchHit[] }) =>
            (res.hits || []).filter((hit) => hit.entity?.type === 'company'),
          ),
          catchError(() => of([] as EntitySearchHit[])),
        );
      }),
    );
  }

  private loadCountries(): void {
    this.entityInfoService.getCountries().subscribe((res) => {
      this.countries = res.entities;

      this.filteredCountries = this.countryControl.valueChanges.pipe(
        startWith(''),
        map((value) => {
          const filterText =
            typeof value === 'string' ? value : value?.name || '';
          return filterText
            ? this.filterEntities(this.countries, filterText)
            : this.countries.slice();
        }),
      );
    });
  }

  private filterEntities(
    entities: GroupingEntity[],
    value: string,
  ): GroupingEntity[] {
    const filterValue = value.toLowerCase().trim();
    if (!filterValue) return entities.slice();
    return entities
      .filter((e) => e.name?.toLowerCase().includes(filterValue))
      .sort((a, b) => {
        const aName = a.name?.toLowerCase() || '';
        const bName = b.name?.toLowerCase() || '';
        const aStarts = aName.startsWith(filterValue);
        const bStarts = bName.startsWith(filterValue);
        if (aStarts !== bStarts) return aStarts ? -1 : 1;
        const aWordStart = aName
          .split(' ')
          .some((w) => w.startsWith(filterValue));
        const bWordStart = bName
          .split(' ')
          .some((w) => w.startsWith(filterValue));
        if (aWordStart !== bWordStart) return aWordStart ? -1 : 1;
        return (a.name || '').localeCompare(b.name || '');
      });
  }
}
